import { useEffect } from "react"

import { useLocalization } from "@izettle/lingo-client"
import {
  IconExportMedium,
  IconHomeMedium,
  IconIdentitycardMedium,
  IconLockMedium,
} from "@izettle/otto"
import { Loader, LoaderSize } from "@izettle/react"

import { Error } from "@/components/error"
import { PageWrapper } from "@/components/layout-helpers"
import { useThemeInfo } from "@/hooks/use-theme-info"
import { Purpose, PurposesContent, ViewedPageEventPayload } from "@/models"
import { usePurposes } from "@/providers/purposes"
import { ViewedPageEvent, sendToTrackingService } from "@/tracking"

import { Done } from "./done"
import { Overview } from "./overview"
import { ThankYou } from "./thank-you"

const getPayloadForViewedPageEvent = (purposes: Purpose[]) => {
  const payload: ViewedPageEventPayload = {}

  purposes.forEach((purpose) => {
    payload[purpose.documentPurposeType] = purpose.status.toUpperCase()
  })

  return payload
}

export const Purposes = () => {
  const { purposes, purposeState, sellerType, getPurposes } = usePurposes()
  const { text } = useLocalization()
  const { paypalTheme } = useThemeInfo()

  useEffect(() => {
    void getPurposes()
  }, [])

  useEffect(() => {
    if (purposeState.kind !== "loading-error") {
      const payload = getPayloadForViewedPageEvent(purposes)
      const page = "DocumentPurposesList"
      sendToTrackingService({ ...ViewedPageEvent, page, payload })
    }
  }, [purposes, purposeState])

  const purposesContent: PurposesContent = {
    new: {
      proofOfAddress: {
        name: text("PROOF_OF_ADDRESS"),
        text: text("PURPOSES_PROOF_OF_ADDRESS_INTRO_TEXT"),
        icon: IconHomeMedium,
      },
      proofOfIdentity: {
        name: text("PROOF_OF_IDENTITY"),
        text: text("PURPOSES_PROOF_OF_IDENTITY_INTRO_TEXT"),
        icon: IconIdentitycardMedium,
      },
      letterOfAuthorization: {
        name: text("PROOF_OF_AUTHORIZATION"),
        text: text("PROOF_OF_AUTHORIZATION_INTRO_TEXT"),
        icon: IconLockMedium,
      },
      genericDocumentUpload: {
        name: text("GENERIC_DOC_UPLOAD"),
        text: text("PURPOSES_GENERIC_DOC_UPLOAD_INTRO_TEXT"),
        icon: IconExportMedium,
      },
    },
    existing: {
      proofOfAddress: {
        name: text("PROOF_OF_ADDRESS"),
        text: text("PURPOSES_PROOF_OF_ADDRESS_INTRO_TEXT_EXISTING_SELLERS"),
        icon: IconHomeMedium,
      },
      proofOfIdentity: {
        name: text("PROOF_OF_IDENTITY"),
        text: text("PURPOSES_PROOF_OF_IDENTITY_INTRO_TEXT"),
        icon: IconIdentitycardMedium,
      },
      letterOfAuthorization: {
        name: text("PROOF_OF_AUTHORIZATION"),
        text: text("PROOF_OF_AUTHORIZATION_INTRO_TEXT"),
        icon: IconLockMedium,
      },
      genericDocumentUpload: {
        name: text("GENERIC_DOC_UPLOAD"),
        text: text("PURPOSES_GENERIC_DOC_UPLOAD_INTRO_TEXT_EXISTING_SELLERS"),
        icon: IconExportMedium,
      },
    },
  }

  if (purposeState.kind === "loading") {
    return (
      <PageWrapper>
        <Loader fullscreen size={LoaderSize.Large} />
      </PageWrapper>
    )
  }

  if (purposeState.kind === "loading-error") {
    return <Error />
  }

  if (purposeState.kind === "completed") {
    return (
      <ThankYou
        purposes={purposes}
        purposesContent={purposesContent}
        sellerType={sellerType}
      />
    )
  }

  if (purposeState.kind === "done") {
    return <Done />
  }

  return (
    <Overview
      purposes={purposes}
      taskRejected={purposeState.kind === "rejected"}
      purposesContent={purposesContent}
      sellerType={sellerType}
      paypalTheme={paypalTheme}
    />
  )
}
